/**
 * Field rules for the auth forms.
 *
 * Kept as plain functions so the rules the browser shows are the same ones
 * the tests assert. The password minimum mirrors Better Auth's own default
 * (`emailAndPassword` length, 8, in better-auth/dist/context/create-context);
 * stating it up front is the only way the user learns it before submitting.
 */

export const MIN_PASSWORD_LENGTH = 8;

/**
 * Deliberately permissive. The authoritative check is the server, and a
 * clever regex here only rejects addresses that are in fact deliverable.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Returns an error message, or null when the value passes. */
export function validateEmail(value: string): string | null {
  const v = value.trim();
  if (v === "") return "Enter your email address";
  if (!EMAIL.test(v)) return "Enter a valid email address, like name@company.com.au";
  return null;
}

export function validatePassword(value: string): string | null {
  if (value === "") return "Enter a password";
  if (value.length < MIN_PASSWORD_LENGTH) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters`;
  }
  return null;
}

/** For sign-in, where the password already exists and length is not ours to judge. */
export function validateRequired(value: string, what: string): string | null {
  return value.trim() === "" ? `Enter your ${what}` : null;
}

/**
 * Where to go after sign-in or sign-up. Only a same-origin path is kept, so a
 * crafted `?redirect=https://…` or `?redirect=//host` cannot send the user off
 * this site.
 */
export function parseAuthSearch(raw: Record<string, unknown>): { redirect?: string } {
  if (typeof raw.redirect === "string" && /^\/(?![/\\])/.test(raw.redirect)) {
    return { redirect: raw.redirect };
  }
  return {};
}

export type Errors<K extends string> = Partial<Record<K, string>>;

/** Drops the null entries so `Object.keys` is exactly the failing fields. */
export function collect<K extends string>(checks: Record<K, string | null>): Errors<K> {
  const out: Errors<K> = {};
  for (const [key, message] of Object.entries(checks) as [K, string | null][]) {
    if (message !== null) out[key] = message;
  }
  return out;
}
